import { useQuery } from '@tanstack/react-query';
import { MenuItem, Stack, TextField, Typography } from '@mui/material';
import { LookupRecord } from '../../../api/masterData';
import { useApplicationFormApi } from './applicationFormApi';
import { SearchableLookupField } from '../../leads/wizard/SearchableLookupField';
import { isoToFlagEmoji } from '../../leads/wizard/wizardUtils';
import { WizardValues } from './wizardTypes';

interface Step2Props {
  values: WizardValues;
  setField: <K extends keyof WizardValues>(field: K, value: WizardValues[K]) => void;
}

export const Step2PersonalDetails = ({ values, setField }: Step2Props) => {
  const { listLookup } = useApplicationFormApi();
  const countriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/countries'],
    queryFn: () => listLookup('/master-data/countries', { activeOnly: true }),
  });

  const flagFor = (option: LookupRecord) =>
    typeof option.iso2 === 'string' ? isoToFlagEmoji(option.iso2) : undefined;

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Surname"
          fullWidth
          required
          value={values.lastName}
          onChange={(e) => setField('lastName', e.target.value)}
        />
        <TextField
          label="First name"
          fullWidth
          required
          value={values.firstName}
          onChange={(e) => setField('firstName', e.target.value)}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Other names"
          fullWidth
          value={values.otherNames}
          onChange={(e) => setField('otherNames', e.target.value)}
        />
        <TextField
          label="Preferred name"
          fullWidth
          value={values.preferredName}
          onChange={(e) => setField('preferredName', e.target.value)}
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          label="Gender"
          fullWidth
          value={values.gender}
          onChange={(e) => setField('gender', e.target.value as WizardValues['gender'])}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          <MenuItem value="MALE">Male</MenuItem>
          <MenuItem value="FEMALE">Female</MenuItem>
          <MenuItem value="OTHER">Other</MenuItem>
        </TextField>
        <TextField
          label="Date of birth"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={values.dateOfBirth}
          onChange={(e) => setField('dateOfBirth', e.target.value)}
        />
        <TextField
          label="Age"
          fullWidth
          disabled
          value={
            values.dateOfBirth
              ? Math.floor((Date.now() - new Date(values.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
              : ''
          }
        />
      </Stack>

      <SearchableLookupField
        label="Nationality"
        options={countriesQuery.data}
        value={values.nationalityCountryId}
        onChange={(id) => setField('nationalityCountryId', id)}
        getFlag={flagFor}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="NIC number"
          fullWidth
          value={values.nicNumber}
          onChange={(e) => setField('nicNumber', e.target.value)}
        />
        <TextField
          label="Passport number"
          fullWidth
          value={values.passportNumber}
          onChange={(e) => setField('passportNumber', e.target.value)}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Passport issue date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={values.passportIssueDate}
          onChange={(e) => setField('passportIssueDate', e.target.value)}
        />
        <TextField
          label="Passport expiry date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={values.passportExpiryDate}
          onChange={(e) => setField('passportExpiryDate', e.target.value)}
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SearchableLookupField
          label="Country of birth"
          options={countriesQuery.data}
          value={values.countryOfBirthId}
          onChange={(id) => setField('countryOfBirthId', id)}
          getFlag={flagFor}
        />
        <TextField
          select
          label="Marital status"
          fullWidth
          value={values.maritalStatus}
          onChange={(e) => setField('maritalStatus', e.target.value as WizardValues['maritalStatus'])}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          <MenuItem value="SINGLE">Single</MenuItem>
          <MenuItem value="MARRIED">Married</MenuItem>
          <MenuItem value="OTHER">Other</MenuItem>
        </TextField>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Email"
          type="email"
          fullWidth
          value={values.email}
          onChange={(e) => setField('email', e.target.value)}
        />
        <TextField
          label="Mobile number"
          fullWidth
          required
          value={values.phone}
          onChange={(e) => setField('phone', e.target.value)}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="WhatsApp number"
          fullWidth
          value={values.whatsappNumber}
          onChange={(e) => setField('whatsappNumber', e.target.value)}
        />
        <TextField
          label="Land number"
          fullWidth
          value={values.telephone}
          onChange={(e) => setField('telephone', e.target.value)}
        />
      </Stack>

      <TextField
        label="Address"
        fullWidth
        multiline
        minRows={2}
        value={values.address}
        onChange={(e) => setField('address', e.target.value)}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField label="City" fullWidth value={values.city} onChange={(e) => setField('city', e.target.value)} />
        <TextField
          label="Postal code"
          fullWidth
          value={values.postalCode}
          onChange={(e) => setField('postalCode', e.target.value)}
        />
        <TextField
          label="Province"
          fullWidth
          value={values.province}
          onChange={(e) => setField('province', e.target.value)}
        />
      </Stack>

      <Typography variant="subtitle2" sx={{ pt: 1 }}>
        Emergency contact
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Name"
          fullWidth
          value={values.emergencyContactName}
          onChange={(e) => setField('emergencyContactName', e.target.value)}
        />
        <TextField
          label="Relationship"
          fullWidth
          value={values.emergencyContactRelationship}
          onChange={(e) => setField('emergencyContactRelationship', e.target.value)}
        />
        <TextField
          label="Phone"
          fullWidth
          value={values.emergencyContactPhone}
          onChange={(e) => setField('emergencyContactPhone', e.target.value)}
        />
      </Stack>
    </Stack>
  );
};
